import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  MegaphoneIcon,
  BriefcaseIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

import {
  ROUTE_ABOUT,
  ROUTE_HELP,
  ROUTE_MY_FIELDS,
  ROUTE_QA,
  ROUTE_RESERVATION,
  ROUTE_SETTINGS,
  ROUTE_TERMS,
  ROUTE_PROBLEM,
  ROUTE_BECOME_PARTNER,
  ROUTE_CONTACT_US,
} from '../../router/constants';
import { useTranslation } from 'react-i18next';
import { useLogout } from '../../hooks/useLogout';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';
import Logo from '../logo/Logo';
import { useResponsiveWindow } from '../../hooks/useResponsiveWindow';

const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const { logout } = useLogout();

  const navigate = useNavigate();
  const location = useLocation();

  const { isTablet } = useResponsiveWindow();

  const getTabFromPath = (path: string) => {
    if (path.includes('/settings')) return 'Settings';
    if (path.includes('/my-fields')) return 'MyFields';
    if (path.includes('/reservation')) return 'Reservation';
    if (path.includes('/help')) return 'Help';
    if (path.includes('/about-us')) return 'About';
    if (path.includes('/logout')) return 'Log Out';
    if (path.includes('/terms-and-conditions')) return 'Terms';
    if (path.includes('/q-and-a')) return 'QA';
    if (path.includes('/problem')) return 'Problem';
    if (path.includes('/become-partner')) return 'Become Partner';
    if (path.includes('/contact-us')) return 'Contact us';
    return '';
  };

  const [selectedTab, setSelectedTab] = useState(
    getTabFromPath(location.pathname)
  );

  useEffect(() => {
    setSelectedTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  const handleTabClick = (tabName: string) => {
    setSelectedTab(tabName);
    if (tabName === 'Settings') {
      navigate(ROUTE_SETTINGS);
    }
    if (tabName === 'MyFields') {
      navigate(ROUTE_MY_FIELDS);
    }
    if (tabName === 'Reservation') {
      navigate(ROUTE_RESERVATION);
    }
    if (tabName === 'About') {
      navigate(ROUTE_ABOUT);
    }
    if (tabName === 'Help') {
      navigate(ROUTE_HELP);
    }
    if (tabName === 'Terms') {
      navigate(ROUTE_TERMS);
    }
    if (tabName === 'QA') {
      navigate(ROUTE_QA);
    }
    if (tabName === 'Problem') {
      navigate(ROUTE_PROBLEM);
    }
    if (tabName === 'Become Partner') {
      navigate(ROUTE_BECOME_PARTNER);
    }
    if (tabName === 'Contact us') {
      navigate(ROUTE_CONTACT_US);
    }
    if (tabName === 'Log Out') {
      logout();
    }
  };

  if (isTablet) {
    return <></>;
  }

  return (
    <div className="flex flex-col justify-between flex-shrink-0 h-screen lg:w-[229px] bg-mainBlack text-mainWhite border-r border-dividerGray sticky overflow-auto">
      <div className="flex flex-col justify-center items-start mt-[36px] ml-[35px] mr-[24px]">
        <div className="mb-2">
          <Logo className="" onClick={() => handleTabClick('MyFields')} />
        </div>
        <div>
          <div
            className="flex flex-row items-center mb-6"
            onClick={() => handleTabClick('Reservation')}
          >
            <img
              src={
                selectedTab === 'Reservation'
                  ? '/icons/calendar-minus-highlighted.svg'
                  : '/icons/calendar-minus.svg'
              }
              alt="Logo"
            />
            <button
              className={`ml-2 text-start ${
                selectedTab === 'Reservation'
                  ? 'text-mainWhite'
                  : 'text-dividerGray'
              } font-inter ${
                selectedTab === 'Reservation' ? 'font-semibold' : 'font-normal'
              } text-base leading-5`}
            >
              {t(COMMON_TRANSLATE_KEYS.RESERVATIONS)}
            </button>
          </div>
          <div
            className="flex flex-row items-center mb-6"
            onClick={() => handleTabClick('MyFields')}
          >
            <img
              src={
                selectedTab === 'MyFields'
                  ? '/icons/basketball-highlighted.svg'
                  : '/icons/basketball.svg'
              }
              alt="Logo"
            />
            <button
              className={`ml-2 text-start ${
                selectedTab === 'MyFields'
                  ? 'text-mainWhite'
                  : 'text-dividerGray'
              } font-inter ${
                selectedTab === 'MyFields' ? 'font-semibold' : 'font-normal'
              } text-base leading-5`}
            >
              {t(COMMON_TRANSLATE_KEYS.MY_FIELDS)}
            </button>
          </div>
          <div
            className="flex flex-row items-center mb-6"
            onClick={() => handleTabClick('Settings')}
          >
            <img
              src={
                selectedTab === 'Settings'
                  ? '/icons/settings-highlighted.svg'
                  : '/icons/settings.svg'
              }
              alt="Logo"
            />
            <button
              className={`ml-2 text-start ${
                selectedTab === 'Settings'
                  ? 'text-mainWhite'
                  : 'text-dividerGray'
              } font-inter ${
                selectedTab === 'Settings' ? 'font-semibold' : 'font-normal'
              } text-base leading-5`}
            >
              {t(COMMON_TRANSLATE_KEYS.SETTINGS)}
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center ml-[35px] mt-[36px] mr-[24px]">
        <div>
          <div
            className="flex flex-row items-center mb-6"
            onClick={() => handleTabClick('About')}
          >
            <InformationCircleIcon
              width={18}
              height={18}
              className={`${
                selectedTab === 'About' ? 'text-mainWhite' : 'text-dividerGray'
              }`}
            />
            <button
              className={`ml-2 text-start ${
                selectedTab === 'About' ? 'text-mainWhite' : 'text-dividerGray'
              } font-inter ${
                selectedTab === 'About' ? 'font-semibold' : 'font-normal'
              } text-base leading-5`}
            >
              {t(COMMON_TRANSLATE_KEYS.ABOUT)}
            </button>
          </div>

          <div
            className="flex flex-row mb-6 items-start"
            onClick={() => handleTabClick('QA')}
          >
            <ChatBubbleLeftRightIcon
              width={20}
              height={20}
              className={`${
                selectedTab === 'QA' ? 'text-mainWhite' : 'text-dividerGray'
              }`}
            />
            <button
              className={`ml-2 text-start ${
                selectedTab === 'QA' ? 'text-mainWhite' : 'text-dividerGray'
              } font-inter ${
                selectedTab === 'QA' ? 'font-semibold' : 'font-normal'
              } text-base leading-5`}
            >
              {t(COMMON_TRANSLATE_KEYS.QA)}
            </button>
          </div>

          <div
            className="flex flex-row mb-6 items-start"
            onClick={() => handleTabClick('Terms')}
          >
            <DocumentTextIcon
              width={18}
              height={18}
              className={`${
                selectedTab === 'Terms' ? 'text-mainWhite' : 'text-dividerGray'
              }`}
            />
            <button
              className={`ml-2 text-start ${
                selectedTab === 'Terms' ? 'text-mainWhite' : 'text-dividerGray'
              } font-inter ${
                selectedTab === 'Terms' ? 'font-semibold' : 'font-normal'
              } text-base leading-5`}
            >
              {t(COMMON_TRANSLATE_KEYS.TERMS)}
            </button>
          </div>

          <div
            className="flex flex-row mb-6 items-start"
            onClick={() => handleTabClick('Contact us')}
          >
            <EnvelopeIcon
              width={18}
              height={18}
              className={`${
                selectedTab === 'Contact us'
                  ? 'text-mainWhite'
                  : 'text-dividerGray'
              }`}
            />
            <button
              className={`ml-2 text-start ${
                selectedTab === 'Contact us'
                  ? 'text-mainWhite'
                  : 'text-dividerGray'
              } font-inter ${
                selectedTab === 'Contact us'
                  ? 'font-semibold'
                  : 'font-normal'
              } text-base leading-5`}
            >
              {t(COMMON_TRANSLATE_KEYS.CONTACT_US)}
            </button>
          </div>

          <div
            className="flex flex-row mb-6 items-start"
            onClick={() => handleTabClick('Become Partner')}
          >
            <BriefcaseIcon
              width={18}
              height={18}
              className={`${
                selectedTab === 'Become Partner'
                  ? 'text-mainWhite'
                  : 'text-dividerGray'
              }`}
            />
            <button
              className={`ml-2 text-start ${
                selectedTab === 'Become Partner'
                  ? 'text-mainWhite'
                  : 'text-dividerGray'
              } font-inter ${
                selectedTab === 'Become Partner'
                  ? 'font-semibold'
                  : 'font-normal'
              } text-base leading-5`}
            >
              {t(COMMON_TRANSLATE_KEYS.BECOME_PARTNER)}
            </button>
          </div>

          <div
            className="flex flex-row items-center mb-6"
            onClick={() => handleTabClick('Help')}
          >
            <img
              src={
                selectedTab === 'Help'
                  ? '/icons/help-highlighted.svg'
                  : '/icons/help.svg'
              }
              alt="Logo"
            />
            <button
              className={`ml-2 text-start ${
                selectedTab === 'Help' ? 'text-mainWhite' : 'text-dividerGray'
              } font-inter ${
                selectedTab === 'Help' ? 'font-semibold' : 'font-normal'
              } text-base leading-5`}
            >
              {t(COMMON_TRANSLATE_KEYS.HELP)}
            </button>
          </div>

          <div
            className="flex flex-row items-start mb-6"
            onClick={() => handleTabClick('Problem')}
          >
            <MegaphoneIcon
              width={20}
              height={20}
              className={`${
                selectedTab === 'Problem'
                  ? 'text-mainWhite'
                  : 'text-dividerGray'
              }`}
            />
            <button
              className={`ml-2 text-start ${
                selectedTab === 'Problem'
                  ? 'text-mainWhite'
                  : 'text-dividerGray'
              } font-inter ${
                selectedTab === 'Problem' ? 'font-semibold' : 'font-normal'
              } text-base leading-5`}
            >
              {t(COMMON_TRANSLATE_KEYS.SIGNAL_PROBLEM)}
            </button>
          </div>

          <div
            className="flex flex-row items-center mb-6"
            onClick={() => handleTabClick('Log Out')}
          >
            <img
              src={
                selectedTab === 'Log Out'
                  ? '/icons/logout-highlighted.svg'
                  : '/icons/logout.svg'
              }
              alt="Logo"
            />
            <button
              className={`ml-2 text-start ${
                selectedTab === 'Log Out'
                  ? 'text-mainWhite'
                  : 'text-dividerGray'
              } font-inter ${
                selectedTab === 'Log Out' ? 'font-semibold' : 'font-normal'
              } text-base leading-5`}
            >
              {t(COMMON_TRANSLATE_KEYS.LOG_OUT)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
